<template>
  <b-container fluid style="margin-bottom: 5em; max-width: 1200px">
    <ulu-customer-header :web_app_id="customer.web_app_id" :brand="customer.brand" :name="customer.company_name"
      :created="customer.created_at" :registration_sent_at="customer.webapp_registration_sent_at"
      :registration_accepted_at="customer.webapp_registration_accepted_at" :company_country="customer.company_country"
      :refresh="fetchCustomer" />
    <b-row style="margin-top: 1em">
      <b-col>
        <b-tabs active-nav-item-class="text-dark" lazy v-model="tab">
          <!-- Refresh Order -->
          <template v-slot:tabs-start>
            <b-nav-item @click.prevent="fetchCustomer" v-b-tooltip.hover :title="$t('globals.reload_customer')">
              <v-icon name="sync-alt" :spin="getShowLoadingModal" color="gray"></v-icon>
            </b-nav-item>
          </template>
          <!-- Customer data -->
          <b-tab :title="$t('globals.data')" title-link-class="text-info">
            <ulu-customer-data :customer="customer" :refresh="fetchCustomer" />
          </b-tab>
          <!-- Customer orders and inquiries -->
          <b-tab :title="$t('globals.orders_inquiries')" title-link-class="text-info">
            <ulu-customer-orders-inquiries :customer="customer.id"
              :total="parseInt(customer.orders_total) + parseInt(customer.inquiries_total)"
              :inquiries="customer.inquiries" />
          </b-tab>
          <!-- Customer devices -->
          <b-tab :title="$t('globals.devices')" title-link-class="text-info">

            <ulu-customer-devices :customer="customer.id" :total="parseInt(customer.devices_total)" />
          </b-tab>
          <!-- Customer invoices -->
          <b-tab :title="$t('globals.invoices')" title-link-class="text-info">
            <ulu-customer-invoices :customer="customer.id" :company_name="customer.company_name"
              :currency="'EUR' /*customer.currency*/" :total="parseInt(customer.invoices_total)" />
          </b-tab>
          <!-- Customer parking invoices -->
          <b-tab :title="$t('globals.parking_invoices')" title-link-class="text-info">
            <ulu-customer-parking-invoices :total="parseInt(customer.parking_invoices_total)" :customer="customer.id"
              :currency="'EUR' /*customer.currency*/" />
          </b-tab>
          <!-- Customer credits -->
          <b-tab :title="$t('globals.credits')" title-link-class="text-info">
            <ulu-customer-credits :customer="customer.id" :total="parseInt(customer.credits_total)"
              :currency="'EUR' /*customer.currency*/" :refresh="fetchCustomer" />
          </b-tab>
          <!-- Sinatec -->
          <b-tab title="Sinatec" title-link-class="text-info">
            <ulu-customer-synatec-orders :customer="customer.id" :total="parseInt(customer.synatec_orders_total)" />
          </b-tab>
          <!-- Customer contract details -->
          <b-tab :title="$t('globals.contract_details')" title-link-class="text-info">
            <ulu-customer-contract-details :notes="customer.notes" :refresh="fetchCustomer"
              :contract_details="customer.customer_contract_detail" :first_order="customer.first_order" />
          </b-tab>
          <!-- Mollie payments, set up  -->
          <b-tab v-if="getUserSystem == 'Cartracker' || getUserSystem == 'traksi'" title="Mollie"
            title-link-class="text-info">
            <ulu-customer-mollie :customer="customer.id" :mollie_payment_order_status="customer.mollie_payment_order_status
              " :mollie_payments="customer.mollie_payments" :refresh="fetchCustomer"
              :total="parseInt(customer.mollie_payments_total)" />
          </b-tab>
          <template #tabs-end>
            <b-nav-item href="#" role="presentation" @click.prevent='$bvModal.show("status-modal")'>
              <span class="text-info">{{ $t('globals.audits') }}</span>
            </b-nav-item>
          </template>
        </b-tabs>
      </b-col>
    </b-row>
    <!-- Audits modal -->
    <b-modal id="status-modal" hide-footer centered size="lg">
      <template v-slot:modal-title>
        {{ $t('globals.audits') }}:
      </template>
      <b-row>
        <b-col>
          <b-table class='text-rq-primary-blue' :items='customer.audits' :fields='auditsFields' sort-by='createdAt'
            :sort-desc='true' bordered small>
          </b-table>
        </b-col>
      </b-row>
    </b-modal>
  </b-container>
</template>

<script>
import Customer from "@/models/customer/customer";
import { mapActions, mapMutations, mapGetters } from "vuex";

export default {
  components: {
    "ulu-customer-header": () =>
      import("@/components/customers/customer/CustomerHeader"),
    "ulu-customer-data": () =>
      import("@/components/customers/customer/CustomerData"),
    "ulu-customer-orders-inquiries": () =>
      import("@/components/customers/customer/CustomerOrdersInquiries"),
    "ulu-customer-invoices": () =>
      import("@/components/customers/customer/CustomerInvoices"),
    "ulu-customer-parking-invoices": () =>
      import("@/components/customers/customer/CustomerParkingInvoices"),
    "ulu-customer-credits": () =>
      import("@/components/customers/customer/CustomerCredit"),
    "ulu-customer-contract-details": () =>
      import("@/components/customers/customer/CustomerContractDetails"),
    "ulu-customer-mollie": () =>
      import("@/components/customers/customer/CustomerMollie"),
    "ulu-customer-devices": () =>
      import("@/components/customers/customer/CustomerDevices"),
    "ulu-customer-synatec-orders": () =>
      import("@/components/customers/customer/CustomerSynatecOrders")
  },
  data() {
    return {
      customer: new Customer(),
      auditsFields: [
        {
          key: 'action',
          label: this.$t('globals.action'),
        },
        {
          key: 'agent.full_name',
          label: this.$t('globals.agent'),
        },
        {
          key: 'created_at',
          label: this.$t('globals.created_at'),
          sortable: true,
        },
      ],
      tab: 0,
    };
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions("axios_mod", ["simpleFetch"]),
    fetchCustomer() {
      const data = {
        query: `
          query {
            Customer (id: ${this.$route.params.id}) {
              notes
              brand
              created_at
              agent {
                full_name
              }
              id
              currency
              web_app_id
              company_name
              company_address
              company_city
              company_postal_code
              company_country
              company_vat
              company_po
              contact_first_name
              contact_last_name
              contact_email
              contact_phone
              contact_address
              contact_city
              contact_postal_code
              contact_country
              shipping_first_name
              shipping_last_name
              shipping_email
              shipping_phone
              shipping_address
              shipping_city
              shipping_postal_code
              shipping_country
              billing_first_name
              billing_last_name
              billing_email
              billing_phone
              billing_address
              billing_city
              billing_postal_code
              billing_country
              block_streatus
              send_to_sharpspring
              customer_type
              category
              source
              webapp_registration_sent_at
              webapp_registration_accepted_at
              customer_settings {
                  invoice_imeis
                  is_whitelabel
                  is_keurmerk
                  driver_id
                  live_driver_id
                  api
                  parking
                  iframe_page
                  sms
                  web_app
                  company_type
              }
              mollie_payment_order_status
              audits {
                agent {
                  full_name
                }
                action
                created_at
              }
              customer_contract_detail{
                id
                contract_type
                start_date
                duration
                termination_policy
                contract_detail_items{
                    id
                    price_one_time
                    price_per_month
                    name
                    item_type
                }
              }
              first_order{
                contract_start_on
                contract_duration
                payment_term_name
                order_type
              }
            }
          }
        `
      };

      this.setShowLoadingModal(true);
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.customer = res.Customer;
          }
        })
        .finally(() => {
          this.setShowLoadingModal(false);
          this.tab = parseInt(this.$route.query.tab) || 0;
        });
    }
  },
  computed: {
    ...mapGetters(["getUserSystem", "getShowLoadingModal"])
  },
  created() {
    this.fetchCustomer();
  },
  watch: {
    tab(n, o) {
      this.$router.replace({ query: { tab: n } }).catch(() => { });
    },
  }
};
</script>